import { Box, Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CustomerCareDTable from '../../../Shared/CustomerCareDTable';
import TabsEmptyState from '../../components/TabsEmptyState';
import { Add as AddIcon } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import DashboardTableLegend from '../../../Dashboard/detailPanel/DashboardTableLegend';
import { ReactComponent as NoHubsIcon } from '../../../../images/icons/hub_assign_failed.svg';
import {
  faCircleCheck,
  faTriangleExclamation,
  faCircleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import StatusChip from '../../../Shared/StatusChip';
import { parseDateToLocal } from '../../../../shared/utils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { TabProps } from '../FacilityDetails';
import {
  getDevicesList,
  getFacilityDevices,
} from '../../../../store/facility/actionCreators';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import NewModal from '../../../Shared/NewModal';
import AddHubToFacilityModal from './AddHubToFacilityModal';
import {
  customError,
  customSuccess,
  errorBg,
  successBg,
  warningBg,
  warningYellow,
} from '../../../../mainTheme';

const statusMap = {
  online: {
    icon: faCircleCheck,
    color: customSuccess,
    bg: successBg,
    label: 'Online',
  },
  offline: {
    icon: faTriangleExclamation,
    color: customError,
    bg: errorBg,
    label: 'Offline',
  },
  unassigned: {
    icon: faCircleExclamation,
    color: warningYellow,
    bg: warningBg,
    label: 'Unassigned',
  },
};

const tableLegendData = [
  {
    name: 'Online',
    color: customSuccess,
  },
  {
    name: 'Offline',
    color: customError,
  },
  {
    name: 'Unassigned',
    color: warningYellow,
  },
];

const HubsTab: React.FC<TabProps> = ({
  parentFacilityId,
  locations,
  locationId,
  isFacilityAdmin,
}) => {
  const dispatch = useDispatch();
  const { devicesList, isLoading, facilityDevices } = useSelector(
    (state: RootState) => state.facility,
  );
  // For admin, use facilityDevices.devices, else use devicesList
  const rows =
    isFacilityAdmin && locationId
      ? facilityDevices?.devices || []
      : devicesList || [];

  const [isAddHubModalOpen, setIsAddHubModalOpen] = useState(false);

  useEffect(() => {
    if (isFacilityAdmin && locationId) {
      dispatch(getFacilityDevices(locationId));
    } else if (parentFacilityId) {
      if (locationId) {
        dispatch(getDevicesList(parentFacilityId, locationId));
      } else {
        dispatch(getDevicesList(parentFacilityId));
      }
    }
  }, [dispatch, parentFacilityId, locationId, isFacilityAdmin]);

  const getLocationName = (location_id: number) => {
    const loc = locations.find((l) => l.id === location_id);
    return loc ? loc.name : 'N/A';
  };

  const columns: GridColDef[] = [
    {
      field: 'patient',
      headerName: 'Patient',
      flex: 1,
      valueGetter: (params: any) => {
        if (isFacilityAdmin && locationId) {
          // admin API: owner is nested
          const owner = params.row.owner;
          return owner
            ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() ||
                'N/A'
            : 'N/A';
        }
        // CC API: top-level
        return params.row.first_name && params.row.last_name
          ? `${params.row.first_name} ${params.row.last_name}`
          : 'N/A';
      },
    },
    {
      field: 'device_status',
      headerName: 'Status',
      flex: 1,
      sortable: true,
      renderCell: (params: any) => {
        const statusValue =
          statusMap[params.value as keyof typeof statusMap] ||
          statusMap['offline'];
        return (
          <Box display="flex" alignItems="center">
            <StatusChip
              icon={statusValue.icon}
              iconColor={statusValue.color}
              labelText={statusValue.label}
              conditionalValue={true}
              backgroundColor={statusValue.bg}
            />
          </Box>
        );
      },
    },
    {
      field: 'serial_number',
      headerName: 'Serial number',
      flex: 1.5,
      valueGetter: (params: any) => params.row.serial_number || 'N/A',
    },
    ...(locationId
      ? []
      : [
          {
            field: 'location_id',
            headerName: 'Location',
            flex: 1.5,
            renderCell: (params: any) => getLocationName(params.value),
          },
        ]),
    {
      field: 'software_version',
      headerName: 'Software version',
      flex: 1.5,
      valueGetter: (params: any) => params.row.software_version || 'N/A',
    },
    {
      field: 'last_seen_online',
      headerName: 'Last online',
      flex: 1.5,
      renderCell: (params: any) => {
        const value = params.value;
        if (!value) return 'N/A';
        // admin API: ISO string, else: can be null or string
        return parseDateToLocal(value, 'yyyy-MM-dd HH:mm:ss');
      },
    },
  ];

  const handleAddHubClick = () => {
    setIsAddHubModalOpen(true);
  };

  const handleCloseAddHubModal = (success?: boolean) => {
    setIsAddHubModalOpen(false);

    // Refresh devices list if modal was closed after successful operation
    if (success && parentFacilityId) {
      if (locationId) {
        dispatch(getDevicesList(parentFacilityId, locationId));
      } else {
        dispatch(getDevicesList(parentFacilityId));
      }
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      {rows && rows.length > 0 ? (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" gap={4}>
              <DashboardTableLegend tableLegendData={tableLegendData} />
            </Box>
            {!isFacilityAdmin && ( // only CC or super admins can add hubs
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                color="secondary"
                onClick={handleAddHubClick}
                sx={{ height: '40px' }}
              >
                Add Hub
              </Button>
            )}
          </Box>
          <CustomerCareDTable
            rows={rows}
            columns={columns}
            pageSize={5}
            defaultSortField="device_status"
            defaultSortDirection="desc"
            hideViewAllButton
            idField="serial_number"
          />
        </>
      ) : (
        <TabsEmptyState
          icon={<NoHubsIcon />}
          title="There are no Hubs"
          description="Assign a Hub to get started."
          buttonText={isFacilityAdmin ? '' : 'Add Hub'} // Empty string will remove the button for location admins to add hub when No data
          onButtonClick={handleAddHubClick}
        />
      )}

      {isAddHubModalOpen && (
        <NewModal
          showCloseX
          title="Add new"
          onClose={handleCloseAddHubModal}
          maxWidth="xs"
        >
          <AddHubToFacilityModal
            onClose={handleCloseAddHubModal}
            locationId={locationId}
            locations={locations}
          />
        </NewModal>
      )}
    </Box>
  );
};

export default HubsTab;
